import api from './api';

export interface CreditoConSaldoPendiente {
  nroFormulario: number;
  nroTipoFormulario?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string;
  importeTotal?: number;
  totalAplicado: number;
  saldoPendiente: number;
}

export interface DebitoConSaldoPendiente {
  nroFormulario: number;
  nroTipoFormulario?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string;
  fechaVencim?: string;
  importeTotal?: number;
  importeCancelado: number;
  montoPendiente: number;
}

export interface AplicacionRecibo {
  nroIdformPagoCobro: number;
  nroIdformulario: number;
  nroTipoFormulario: string;
  letra: string;
  nroSuc?: number;
  nroComprobante: number;
  fechaEmision: string;
  fechaVencim: string;
  importeTotal?: number;
  importeCanceladoAnt?: number;
  fecha?: string;
  nroMovCaja: number;
  montoCancelado?: number;
}

export interface DocumentoAplicar {
  nroIdformulario: number;
  montoCancelado: number;
}

/** Crédito que imputó en un débito de cliente (vista desde factura / ND) */
export interface AplicacionCreditoEnDebito {
  nroIdformPagoCobro: number;
  nroTipoFormularioCredito: string;
  letra: string;
  nroSuc: number;
  nroComprobante: number;
  nroIdformularioDebito: number;
  fechaEmisionDebito: string;
  nroMovCaja: number;
  montoCancelado?: number;
  fechaAplicacion?: string;
}

export interface DesaplicarRequest {
  nroIdformulario: number;
  fechaEmision: string;
  nroMovCaja: number;
}

const tesoreriaAplicacionesService = {
  getCreditosConSaldoPendiente: async (nroCliente: number): Promise<CreditoConSaldoPendiente[]> => {
    const response = await api.get<CreditoConSaldoPendiente[]>(
      `/api/TesoreriaAplicaciones/clientes/${nroCliente}/creditos-con-saldo-pendiente`
    );
    return response.data ?? [];
  },

  getDebitosConSaldoPendiente: async (nroCliente: number): Promise<DebitoConSaldoPendiente[]> => {
    const response = await api.get<DebitoConSaldoPendiente[]>(
      `/api/TesoreriaAplicaciones/clientes/${nroCliente}/debitos-con-saldo-pendiente`
    );
    return response.data ?? [];
  },

  getAplicacionesRecibo: async (reciboId: number): Promise<AplicacionRecibo[]> => {
    const response = await api.get<AplicacionRecibo[]>(
      `/api/TesoreriaAplicaciones/recibos/${reciboId}/aplicaciones`
    );
    return response.data ?? [];
  },

  getAplicacionesCreditoEnDebitoCliente: async (
    nroCliente: number,
    nroFormularioDebito: number
  ): Promise<AplicacionCreditoEnDebito[]> => {
    const response = await api.get<AplicacionCreditoEnDebito[]>(
      `/api/TesoreriaAplicaciones/clientes/${nroCliente}/debitos/${nroFormularioDebito}/aplicaciones-credito`
    );
    return response.data ?? [];
  },

  aplicarSaldoRecibo: async (
    reciboId: number,
    documentosAplicar: DocumentoAplicar[]
  ): Promise<void> => {
    await api.post(`/api/TesoreriaAplicaciones/recibos/${reciboId}/aplicar-saldo`, {
      documentosAplicar,
    });
  },

  desaplicarRecibo: async (
    reciboId: number,
    request: DesaplicarRequest
  ): Promise<void> => {
    await api.delete(`/api/TesoreriaAplicaciones/recibos/${reciboId}/aplicaciones`, {
      data: request,
    });
  },

  /** Mismo contrato que cliente; `nroProveedor` es Formulario.NroCliente con TipoCliente P */
  getCreditosProveedorConSaldoPendiente: async (nroProveedor: number): Promise<CreditoConSaldoPendiente[]> => {
    const response = await api.get<CreditoConSaldoPendiente[]>(
      `/api/TesoreriaAplicaciones/proveedores/${nroProveedor}/creditos-con-saldo-pendiente`
    );
    return response.data ?? [];
  },

  getDebitosProveedorConSaldoPendiente: async (nroProveedor: number): Promise<DebitoConSaldoPendiente[]> => {
    const response = await api.get<DebitoConSaldoPendiente[]>(
      `/api/TesoreriaAplicaciones/proveedores/${nroProveedor}/debitos-con-saldo-pendiente`
    );
    return response.data ?? [];
  },

  getAplicacionesPagoProveedor: async (nroFormularioCredito: number): Promise<AplicacionRecibo[]> => {
    const response = await api.get<AplicacionRecibo[]>(
      `/api/TesoreriaAplicaciones/proveedores/pagos/${nroFormularioCredito}/aplicaciones`
    );
    return response.data ?? [];
  },

  aplicarSaldoPagoProveedor: async (
    nroFormularioCredito: number,
    documentosAplicar: DocumentoAplicar[]
  ): Promise<void> => {
    await api.post(`/api/TesoreriaAplicaciones/proveedores/pagos/${nroFormularioCredito}/aplicar-saldo`, {
      documentosAplicar,
    });
  },

  desaplicarPagoProveedor: async (
    nroFormularioCredito: number,
    request: DesaplicarRequest
  ): Promise<void> => {
    await api.delete(`/api/TesoreriaAplicaciones/proveedores/pagos/${nroFormularioCredito}/aplicaciones`, {
      data: request,
    });
  },
};

export default tesoreriaAplicacionesService;
